import React from 'react';
import { Button, Card, CardFooter, Link, Text, Title3, makeStyles, tokens } from '@fluentui/react-components';
import { IDocumentDetails } from '../../../@types/models';
import { t } from 'i18next';
import { openDocumentLink } from '../../../utils/openDocumentLink';

const ARCHIVE_CODE_IMPORT_ARCHIVE = 'Import archive';
const useStyles = makeStyles({
    root: {
        width: '100%',
        padding: tokens.spacingVerticalS,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXS,
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    card: {
        paddingTop: tokens.spacingVerticalXS,
        paddingBottom: tokens.spacingVerticalXS,
    },
    cardRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: tokens.spacingHorizontalS,
    },
    cardText: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXS,
        flex: 1,
        minWidth: 0,
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: 0,
    },
    meta: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase100,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
    },
    title: {
        fontSize: tokens.fontSizeBase200,
    },
    heading: {
        fontSize: tokens.fontSizeBase300,
    },
    subheading: {
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
    },
});

interface MultiMailArchiveDetailsProps {
    documents: IDocumentDetails[];
    onOpenUnregisteredDesktop?: () => void;
}

export const MultiMailArchiveDetails: React.FC<MultiMailArchiveDetailsProps> = ({ documents, onOpenUnregisteredDesktop }) => {
    const styles = useStyles();
    const documentLabel = 'Document';

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <Title3 className={styles.heading}>{t('Already archived emails')}</Title3>
                <Text className={styles.subheading}>{t('These are unregistered or case documents.')}</Text>
                {onOpenUnregisteredDesktop && (
                    <Link as="button" onClick={onOpenUnregisteredDesktop}>
                        {t('Open unregistered desktop')}
                    </Link>
                )}
            </div>
            <div className={styles.list}>
                {documents.map((document, index) => {
                    const documentNumber = document.DocumentNumber ?? document.Documentnumber;
                    const docTitle = document.DocumentTitle || `${documentLabel} ${index + 1}`;
                    const archiveCode = document.ArchiveCode || '';
                    const metaText = [archiveCode, documentNumber].filter(Boolean).join(' : ');
                    const ariaLabel = [docTitle, metaText].filter(Boolean).join(' - ');

                    return (
                        <Card className={styles.card} key={`${document.DocumentRecno ?? documentNumber ?? 'document'}-${index}`}>
                            <div className={styles.cardRow}>
                                <div className={styles.cardText}>
                                    <Text className={styles.title} weight="semibold">{docTitle}</Text>
                                    {metaText && <Text className={styles.meta}>{metaText}</Text>}
                                </div>
                                <CardFooter className={styles.footer}>
                                    <Button
                                        size="small"
                                        appearance="primary"
                                        aria-label={ariaLabel}
                                        onClick={() => openDocumentLink(document.DocumentDetailsLink, document.DocumentRecno, document.ArchiveCode)}
                                    >
                                        {document.ArchiveCode == ARCHIVE_CODE_IMPORT_ARCHIVE ? t('Register') : t('Open')}
                                    </Button>
                                </CardFooter>
                            </div>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
};
